import { alpha, type Theme, useTheme } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, NEUTRAL, SURFACE } from "@/theme/tokens";
import { type SystemChipItem, SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SHIFT_DETAIL_LABEL_KEYS, SHIFT_DETAIL_TEST_IDS, SHIFT_DETAILS, type ShiftDetail } from "./addStaffUtils";

type Props = {
	value: ShiftDetail;
	onChange: (detail: ShiftDetail) => void;
};

const CHIP_SX = { height: CONTROL_HEIGHT.COMPACT, px: 0.5, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 500 } as const;

/**
 * Picked = brand ring over a soft brand wash; resting = white with a hairline, so the one picked pill is the
 * only tinted thing on the row. Plain objects: `SystemChips` spreads these into its own `sx` object.
 */
const buildChipSx = (theme: Theme) => ({
	selected: {
		...CHIP_SX,
		"&.MuiChip-root, &.MuiChip-root:hover": {
			borderColor: theme.palette.primary.main,
			backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY),
			color: theme.palette.primary.dark,
		},
	},
	unselected: {
		...CHIP_SX,
		"&.MuiChip-root": { borderColor: NEUTRAL[400], backgroundColor: SURFACE, color: INK.SECONDARY },
		"&.MuiChip-root:hover": { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY) },
	},
});

/**
 * "Shift details": None / Charge / Trainee / Orientation / Education — exactly one, applied to every picked
 * nurse. Re-clicking the picked chip keeps it, so there is always an answer (None is the explicit "plain").
 */
export const ShiftDetailsChips: FC<Props> = ({ value, onChange }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const chipSx = useMemo(() => buildChipSx(theme), [theme]);
	const items = useMemo<SystemChipItem<ShiftDetail>[]>(
		() => SHIFT_DETAILS.map((detail) => ({ value: detail, label: t(SHIFT_DETAIL_LABEL_KEYS[detail]) })),
		[t],
	);
	const selectedItems = useMemo(() => items.filter((item) => item.value === value), [items, value]);
	return (
		<SystemChips<ShiftDetail>
			items={items}
			selectedItems={selectedItems}
			onChange={(item, selected) => selected && onChange(item.value)}
			variant="outlined"
			selectedSx={chipSx.selected}
			unselectedSx={chipSx.unselected}
			forceFitContent
			touchTarget
			dataTestId="add-nurse-shift-details"
			getItemDataTestId={(item) => SHIFT_DETAIL_TEST_IDS[item.value]}
		/>
	);
};
